import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { Trans } from '@lingui/react/macro';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { DataTable } from '@duke-hq/ui/components/DataTable/index';
import { useForm } from 'react-hook-form';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import type {
  MigrationInput,
  MigrationInputField,
} from '@duke-hq/svc-api/admin/trpc/routers/migrations/types';
import { DataTableCellAction, DataTableSchema } from '@duke-hq/ui/components/DataTable/schema';
import { MigrationRuns } from './MigrationRuns';
import { formatLastRunAt } from './formatLastRunAt';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Center,
  Collapse,
  FormControl,
  Input,
  Stack,
  Button,
  Divider,
  Text,
  Textarea,
  Checkbox,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  HStack,
  Link,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { SortOrder } from '@duke-hq/lib/pagination';
import { withPermission } from '../withPermission';

// Highlights the row under the pointer, so it's clear which migration a button
// belongs to.
const rowHoverSx = { 'tbody tr:hover': { bg: 'bg.subtle' } };

export const DataMigrationsPage = withPermission('canAccessDeveloperTools', () => {
  return <DataMigrationsTable />;
});

export function DataMigrationsTable() {
  const [sortOrder, setSortOrder] = useState<{
    [key: string]: SortOrder | undefined;
  }>({});
  const [runProc, setRunProc] = useState<
    | { procedure: string; description?: string; ticket?: string; staticForm?: MigrationInput }
    | undefined
  >(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);
  // Not the mutation's isPending: that locks an unrelated migration's modal.
  const [runningProcedure, setRunningProcedure] = useState<string | undefined>(undefined);
  const toast = useToast();
  const utils = trpcAdmin.useUtils();

  const { selectedProcedureName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const onCloseDrawer = useCallback(() => {
    navigate(`/developer-tools/data-migrations/?${searchParams.toString()}`);
  }, [navigate, searchParams]);

  // A search also looks through archived migrations, so an old run can be
  // found by name without expanding the section first.
  const isArchivedExpanded = isArchivedOpen || searchQuery.trim() !== '';

  const { data: migrationsData, refetch } = trpcAdmin.migrations.list.useQuery(
    {
      table: {
        sortOrder,
      },
      search: searchQuery.trim() ?? undefined,
      includeArchived: isArchivedExpanded,
    },
    {
      // Keeps the table on screen while the archived rows load.
      placeholderData: keepPreviousData,
      // Runs finish in the background, so keep checking until none are left.
      refetchInterval: (query) => (query.state.data?.hasRunningMigration === true ? 5000 : false),
    },
  );

  // One column at a time: the table draws no multi-sort affordance.
  const onSortChange = useCallback(
    (columnName: string, direction?: SortOrder) =>
      setSortOrder(direction === undefined ? {} : { [columnName]: direction }),
    [setSortOrder],
  );

  const runMigration = trpcAdmin.migrations.run.useMutation({
    // Picks up the new RUNNING row, which also starts the polling.
    onSettled: () => {
      void refetch();
    },
  });

  const runWithFeedback = useCallback(
    async (procedure: string, data?: Record<string, unknown>): Promise<boolean> => {
      setRunningProcedure(procedure);
      try {
        await runMigration.mutateAsync({ procedure, data });
        toast({
          title: `${procedure} started`,
          description: 'Its status updates here when it finishes. Open History for the result.',
          status: 'info',
          duration: 8000,
          isClosable: true,
        });
        return true;
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : `${procedure} failed to start`,
          status: 'error',
          duration: 10000,
          isClosable: true,
        });
        return false;
      } finally {
        setRunningProcedure(undefined);
      }
    },
    [runMigration, toast],
  );

  const handleAction = useCallback(
    async (
      action: DataTableCellAction<{
        procedure: string;
        description?: string;
        ticket?: string;
        form?: MigrationInput;
        needsFormFetch?: boolean;
      }>,
    ) => {
      switch (action.kind) {
        case 'run.migration': {
          if (
            action.data !== null &&
            'procedure' in action.data &&
            typeof action.data['procedure'] === 'string'
          ) {
            const { procedure, description, ticket, form, needsFormFetch } = action.data;
            if (form !== undefined) {
              setRunProc({ procedure, description, ticket, staticForm: form });
            } else if (needsFormFetch === true) {
              void utils.migrations.getRunForm.prefetch({ procedure });
              setRunProc({ procedure, description, ticket });
            } else {
              setRunProc({ procedure, description, ticket, staticForm: {} });
            }
          }
          break;
        }
        case 'view.runs': {
          if (
            action.data !== null &&
            'procedure' in action.data &&
            typeof action.data['procedure'] === 'string'
          ) {
            navigate(
              `/developer-tools/data-migrations/${action.data.procedure}?${searchParams.toString()}`,
            );
          }
          break;
        }
      }
    },
    [navigate, searchParams, utils],
  );

  const isRunProcRunning = typeof runProc !== 'undefined' && runningProcedure === runProc.procedure;

  const tables = useMemo(() => {
    if (typeof migrationsData === 'undefined') {
      return;
    }

    const headers = {
      ...migrationsData.headers,
      view: {
        label: 'History',
        sortable: false,
      },
    } satisfies DataTableSchema['headers'];
    const withViewAction = (rows: typeof migrationsData.rows) =>
      rows.map(
        (row) =>
          ({
            id: row.id,
            cells: {
              ...row.cells,
              // Formatted here so "Today 10:42" uses the viewer's time zone.
              // A never-run migration carries the epoch and keeps its "Never".
              lastRun:
                row.cells.lastRun.value.getTime() === 0
                  ? row.cells.lastRun
                  : { ...row.cells.lastRun, label: formatLastRunAt(row.cells.lastRun.value) },
              view: {
                type: 'action',
                label: 'View',
                kind: 'view.runs',
                data: {
                  procedure: row.cells.name.value,
                },
                size: 'sm',
                variant: 'outline',
                colorScheme: 'gray',
              },
            },
          }) satisfies DataTableSchema['rows'][number],
      );

    return {
      live: { headers, rows: withViewAction(migrationsData.rows) } satisfies DataTableSchema,
      archived: {
        headers,
        rows: withViewAction(migrationsData.archivedRows),
      } satisfies DataTableSchema,
    };
  }, [migrationsData]);

  if (typeof migrationsData === 'undefined' || typeof tables === 'undefined') {
    return (
      <Center h="full">
        <Spinner />
      </Center>
    );
  }

  return (
    <>
      <TableViewLayout
        pageHeader={[
          <PageHeading key="data-migrations">
            <Trans>Data Migrations</Trans>
          </PageHeading>,
        ]}
        filters={
          <SearchInput
            maxW="search-input"
            onCommit={setSearchQuery}
            placeholder="Search migrations"
          />
        }
        tableActionsLeft={null}
        table={
          <Stack spacing={8} pb={12}>
            <DataTable
              data={tables.live}
              onSortChange={onSortChange}
              handleAction={handleAction}
              tableProps={{
                colorScheme: 'purple',
                size: 'sm',
                sx: rowHoverSx,
              }}
            />
            {migrationsData.archivedCount > 0 ? (
              <Stack spacing={2}>
                <Box px={4}>
                  <Button
                    variant="ghost"
                    size="sm"
                    ml={-2}
                    leftIcon={
                      isArchivedExpanded ? (
                        <ChevronDown size={iconSize.md} aria-hidden />
                      ) : (
                        <ChevronRight size={iconSize.md} aria-hidden />
                      )
                    }
                    aria-expanded={isArchivedExpanded}
                    onClick={() => setIsArchivedOpen(!isArchivedExpanded)}
                  >
                    Archived ({migrationsData.archivedCount})
                  </Button>
                  <Text fontSize="sm" color="fg.muted">
                    Removed from the codebase. Run history only, these can&apos;t be run again.
                  </Text>
                </Box>
                <Collapse in={isArchivedExpanded} animateOpacity>
                  <DataTable
                    data={tables.archived}
                    onSortChange={onSortChange}
                    handleAction={handleAction}
                    tableProps={{
                      colorScheme: 'gray',
                      size: 'sm',
                      sx: rowHoverSx,
                    }}
                  />
                </Collapse>
              </Stack>
            ) : null}
          </Stack>
        }
        pagination={null}
      />
      <MigrationRuns
        procedureName={selectedProcedureName}
        isOpen={typeof selectedProcedureName === 'string'}
        onClose={onCloseDrawer}
      />
      <FormModalWrapper
        modalContentHeight="auto"
        isOpen={typeof runProc !== 'undefined'}
        modalCloseButton={!isRunProcRunning}
        onClose={() => {
          if (!isRunProcRunning) {
            setRunProc(undefined);
          }
        }}
        modalContent={
          typeof runProc !== 'undefined' ? (
            <RunMigrationModalContent
              heading={
                <RunMigrationHeading
                  procedure={runProc.procedure}
                  description={runProc.description}
                  ticket={runProc.ticket}
                  environment={migrationsData.environment}
                />
              }
              procedure={runProc.procedure}
              staticForm={runProc.staticForm}
              isRunning={isRunProcRunning}
              onSubmit={async (data) => {
                if (await runWithFeedback(runProc.procedure, data)) {
                  setRunProc(undefined);
                }
              }}
            />
          ) : null
        }
      />
    </>
  );
}

function RunMigrationHeading({
  procedure,
  description,
  ticket,
  environment,
}: {
  procedure: string;
  description: string | undefined;
  ticket: string | undefined;
  environment: string;
}) {
  return (
    <Stack spacing={2}>
      <HStack justify="space-between" align="center">
        <Text fontSize="lg" fontFamily="monospace" fontWeight="bold">
          {procedure}
        </Text>
        <Badge
          colorScheme={environment === 'Production' ? 'red' : 'orange'}
          variant="solid"
          fontSize="sm"
        >
          {environment}
        </Badge>
      </HStack>
      {description !== undefined ? (
        <Text fontSize="sm" color="fg.muted">
          {description}
        </Text>
      ) : null}
      {ticket !== undefined ? (
        <Link
          href={`https://linear.app/duke-hq/issue/${ticket}`}
          isExternal
          fontSize="sm"
          color="fg.accent.emphasis"
        >
          {ticket}
        </Link>
      ) : null}
    </Stack>
  );
}

function RunMigrationModalContent({
  heading,
  procedure,
  staticForm,
  isRunning,
  onSubmit,
}: {
  heading: React.ReactNode;
  procedure: string;
  staticForm: MigrationInput | undefined;
  isRunning: boolean;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
}) {
  const {
    data: fetchedForm,
    error,
    refetch,
    isFetching,
  } = trpcAdmin.migrations.getRunForm.useQuery(
    { procedure },
    { enabled: staticForm === undefined, retry: false },
  );

  const form = staticForm ?? fetchedForm;

  if (form !== undefined) {
    return <InputForm fields={form} heading={heading} isRunning={isRunning} onSubmit={onSubmit} />;
  }

  return (
    <Stack style={{ width: '100%', padding: '1rem' }} spacing={4}>
      {heading}
      <Divider />
      {error ? (
        <Stack spacing={4}>
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
          <Button
            onClick={() => {
              void refetch();
            }}
            isLoading={isFetching}
            colorScheme="purple"
            alignSelf="flex-start"
            size="sm"
          >
            Retry
          </Button>
        </Stack>
      ) : (
        <Stack align="center" py={8} spacing={3}>
          <Spinner color="purple.500" size="lg" />
          <Text fontSize="sm" color="fg.muted">
            Loading form…
          </Text>
        </Stack>
      )}
    </Stack>
  );
}

function InputForm({
  onSubmit,
  heading,
  fields,
  isRunning,
}: {
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  heading: React.ReactNode;
  fields: MigrationInput;
  isRunning: boolean;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<Record<string, string | string[] | boolean | number | undefined>>();

  const dryRunField = fields['dryRun'];
  const isRealRun =
    dryRunField?.type !== 'boolean' || watch('dryRun', dryRunField.defaultValue ?? true) === false;

  const handleSelectAll = useCallback(
    (
      fieldKey: string,
      options: (string | { label: string; value: string })[],
      isChecked: boolean,
    ) => {
      if (isChecked) {
        const allValues = options.map((option) =>
          typeof option === 'string' ? option : option.value,
        );
        setValue(fieldKey, allValues, { shouldValidate: true });
      } else {
        setValue(fieldKey, [] satisfies string[], { shouldValidate: true });
      }
    },
    [setValue],
  );

  const handleCheckboxChange = useCallback(
    (fieldKey: string, value: string, isChecked: boolean) => {
      const watchedValue = watch(fieldKey);
      const currentValues = Array.isArray(watchedValue) ? watchedValue : [];
      if (isChecked) {
        setValue(fieldKey, [...currentValues, value], { shouldValidate: true });
      } else {
        setValue(
          fieldKey,
          currentValues.filter((v) => v !== value),
          { shouldValidate: true },
        );
      }
    },
    [watch, setValue],
  );

  return (
    <form style={{ width: '100%', padding: '1rem' }} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box mb={4}>{heading}</Box>
      <Stack spacing={4}>
        <Divider />
        {Object.keys(fields).length === 0 ? (
          <Text>This migration has no settings. It runs as soon as you click Run.</Text>
        ) : null}
        {Object.entries(fields).map(([key, maybeField]) => {
          const field = isMigrationInputField(maybeField) ? maybeField : null;
          if (field === null) {
            return null;
          }

          const watchedValues = watch(key) || [];
          const watchedValuesArray =
            field.type === 'checkbox' && Array.isArray(watchedValues) ? watchedValues : [];

          const allOptionValues =
            field.type === 'checkbox'
              ? field.options.map((option) => (typeof option === 'string' ? option : option.value))
              : [];
          const isAllSelected =
            field.type === 'checkbox'
              ? allOptionValues.length > 0 &&
                allOptionValues.every((value) => watchedValuesArray.includes(value))
              : false;
          const isIndeterminate =
            field.type === 'checkbox'
              ? watchedValuesArray.length > 0 && watchedValuesArray.length < allOptionValues.length
              : false;

          switch (field.type) {
            case 'checkbox': {
              return (
                <FormControl
                  key={key}
                  isInvalid={!!errors[key]}
                  isRequired={field.required === true}
                >
                  <FormLabel>{field.label ?? ''}</FormLabel>
                  <FormHelperText>{field.description ?? ''}</FormHelperText>
                  <Stack mt={4}>
                    <Checkbox
                      isChecked={isAllSelected}
                      isIndeterminate={isIndeterminate}
                      onChange={(e) => handleSelectAll(key, field.options, e.target.checked)}
                      fontWeight="semibold"
                      borderColor="purple.300"
                    >
                      Select all ({field.options.length})
                    </Checkbox>
                    <Divider />
                    <Box maxH="20rem" overflowY="auto" pr={2}>
                      <Stack>
                        {field.options.map((option) => {
                          const optionValue = typeof option === 'string' ? option : option.value;
                          const optionLabel = typeof option === 'string' ? option : option.label;
                          return (
                            <Checkbox
                              key={optionValue}
                              value={optionValue}
                              isChecked={watchedValuesArray.includes(optionValue)}
                              onChange={(e) =>
                                handleCheckboxChange(key, optionValue, e.target.checked)
                              }
                              pl={4}
                            >
                              {optionLabel}
                            </Checkbox>
                          );
                        })}
                      </Stack>
                    </Box>
                    {/* Hidden input for validation */}
                    <input
                      type="hidden"
                      {...register(key, {
                        required: field.required === true ? 'This field is required' : false,
                        validate: (value) => {
                          if (
                            field.required === true &&
                            (value == null || (Array.isArray(value) && value.length === 0))
                          ) {
                            return 'This field is required';
                          }
                          return true;
                        },
                      })}
                    />
                  </Stack>
                  <FormErrorMessage>{errors[key]?.message}</FormErrorMessage>
                </FormControl>
              );
            }
            case 'text':
              return (
                <FormControl
                  key={key}
                  isInvalid={!!errors[key]}
                  isRequired={field.required === true}
                >
                  <FormLabel>{field.label ?? ''}</FormLabel>
                  <Input
                    id={key}
                    defaultValue={field.defaultValue}
                    {...register(key, {
                      required: field.required === true ? 'This field is required' : false,
                    })}
                  />
                  <FormHelperText>{field.description ?? ''}</FormHelperText>
                  <FormErrorMessage>{errors[key]?.message}</FormErrorMessage>
                </FormControl>
              );
            case 'number': {
              const { min } = field;
              return (
                <FormControl
                  key={key}
                  isInvalid={!!errors[key]}
                  isRequired={field.required === true}
                >
                  <FormLabel>{field.label ?? ''}</FormLabel>
                  <Input
                    id={key}
                    type="number"
                    inputMode="numeric"
                    step={1}
                    min={min}
                    defaultValue={field.defaultValue}
                    {...register(key, {
                      required: field.required === true ? 'This field is required' : false,
                      // An empty box sends nothing, so the migration's own default applies.
                      setValueAs: (value: string) => (value === '' ? undefined : Number(value)),
                      validate: (value) => {
                        if (value === undefined) {
                          return true;
                        }
                        if (typeof value !== 'number' || !Number.isInteger(value)) {
                          return 'Enter a whole number';
                        }
                        if (min !== undefined && value < min) {
                          return `Enter ${min} or more`;
                        }
                        return true;
                      },
                    })}
                  />
                  <FormHelperText>{field.description ?? ''}</FormHelperText>
                  <FormErrorMessage>{errors[key]?.message}</FormErrorMessage>
                </FormControl>
              );
            }
            case 'textarea':
              return (
                <FormControl
                  key={key}
                  isInvalid={!!errors[key]}
                  isRequired={field.required === true}
                >
                  <FormLabel>{field.label ?? ''}</FormLabel>
                  <Textarea
                    id={key}
                    defaultValue={field.defaultValue}
                    {...register(key, {
                      required: field.required === true ? 'This field is required' : false,
                    })}
                  />
                  <FormHelperText>{field.description ?? ''}</FormHelperText>
                  <FormErrorMessage>{errors[key]?.message}</FormErrorMessage>
                </FormControl>
              );
            case 'boolean':
              return (
                <FormControl
                  key={key}
                  isInvalid={!!errors[key]}
                  isRequired={field.required === true}
                >
                  <Checkbox
                    id={key}
                    // A dry run starts ticked, so submitting an untouched form never writes.
                    defaultChecked={field.defaultValue ?? key === 'dryRun'}
                    {...register(key, {
                      required: field.required === true ? 'This field is required' : false,
                    })}
                  >
                    {field.label}
                  </Checkbox>
                  <FormHelperText>{field.description ?? ''}</FormHelperText>
                  <FormErrorMessage>{errors[key]?.message}</FormErrorMessage>
                </FormControl>
              );
            default:
              return null;
          }
        })}
        <Button
          type="submit"
          colorScheme={isRealRun ? 'red' : 'purple'}
          size="sm"
          isLoading={isRunning}
          loadingText="Starting…"
        >
          {isRealRun ? 'Run for real (writes data)' : 'Run'}
        </Button>
      </Stack>
    </form>
  );
}

function isMigrationInputField(input: unknown): input is MigrationInputField {
  return typeof input === 'object' && input !== null && 'type' in input;
}
